import React, { useState, useEffect, useContext, useRef } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import UserNavbar from './components/UserNavbar';
import UserLeftMenu from './components/UserLeftMenu';
import Announcement from './components/Announcement';
import DraggableSection from './components/DraggableSection';
import Profile from '../../components/Profile';
import { AuthContext } from '../../context/AuthContext';
import userPreferenceService from '../../services/userPreferenceService';
import desktopService from '../../services/desktopService';
import userPanelPreferenceService from '../../services/userPanelPreferenceService';
import AccessControlService from '../../services/AccessControlService';
import DesktopAccessControlService from '../../services/DesktopAccessControlService';
import SettingsService from '../../services/SettingsService';
import { API_BASE_URL } from '../../configs/apiConfig';
import { menuConfig } from '../../configs/menuConfig';
import Spinner from '../../components/Spinner';
import * as FaIcons from 'react-icons/fa';
import { FaTh } from 'react-icons/fa';
import './styles/UserDashboard.css';
import './styles/Modal.css';
import './styles/global.css';
import './styles/Tooltip.css';

// Add this new component before the UserDashboard component
const ImageViewer = ({ url }) => {
    const [scale, setScale] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    const handleZoomIn = () => {
        setScale(prev => prev + 0.5);
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(1, prev - 0.5));
        if (scale <= 1) {
            setPosition({ x: 0, y: 0 });
        }
    };

    const handleMouseDown = (e) => {
        if (scale > 1) {
            e.preventDefault();
            setIsDragging(true);
            setStartPosition({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            const x = e.clientX - startPosition.x;
            const y = e.clientY - startPosition.y;
            setPosition({ x, y });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, startPosition]);

    return (
        <div className={`file-viewer image-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
            <div 
                ref={containerRef}
                style={{ 
                    width: '100%',
                    height: '80vh',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: 'rgba(0,0,0,0.02)',
                    borderRadius: '8px',
                    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={handleMouseDown}
            >
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    <img 
                        ref={imageRef}
                        src={`${API_BASE_URL}${url}`} 
                        alt="File content"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                            transformOrigin: '50% 50%',
                            userSelect: 'none',
                            transition: scale === 1 ? 'transform 0.3s ease' : 'none'
                        }}
                    />
                </div>
            </div>
            <div className="image-viewer-controls">
                <button onClick={handleZoomIn} title="Zoom In">
                    <FaIcons.FaSearchPlus />
                </button>
                <button onClick={handleZoomOut} title="Zoom Out">
                    <FaIcons.FaSearchMinus />
                </button>
                <button onClick={handleFullscreen} title="Toggle Fullscreen">
                    {isFullscreen ? <FaIcons.FaCompress /> : <FaIcons.FaExpand />}
                </button>
            </div>
        </div>
    );
};

const UserDashboard = () => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
    const [userPreferences, setUserPreferences] = useState({
        backgroundImage: '',
        darkMode: false,
        formOpacity: 0.85,
        formBlur: 1.0,
        backgroundBlur: 0.0,
        menuCollapsed: false,
        baseColor: null
    });
    const [panels, setPanels] = useState([]);
    const [sectionVisibility, setSectionVisibility] = useState({});
    const [showAnnouncement, setShowAnnouncement] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isChangingDesktop, setIsChangingDesktop] = useState(false);
    const [desktops, setDesktops] = useState([]);
    const [currentDesktop, setCurrentDesktop] = useState(null);
    const [accessiblePages, setAccessiblePages] = useState([]);
    const [iframeLoading, setIframeLoading] = useState(false);
    const [externalLinkError, setExternalLinkError] = useState(false);
    const iframeRef = useRef(null);
    const [landingItems, setLandingItems] = useState([]);
    const [selectedLandingItem, setSelectedLandingItem] = useState(null);
    const [currentPath, setCurrentPath] = useState([]);
    const [currentItems, setCurrentItems] = useState([]);
    const [showLandingContent, setShowLandingContent] = useState(false);
    
    // Separate states for landing items
    const [landingIframeLoading, setLandingIframeLoading] = useState(false);
    const [landingIframeError, setLandingIframeError] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);

    useEffect(() => {
        // Helper function to get preferences with fallback
        const getPreferencesWithFallback = async (userId) => {
            try {
                // Try to get user preferences first
                const preferences = await userPreferenceService.getUserPreference(userId);
                console.log('Found user preferences for', userId, preferences);
                
                // Get default settings regardless of whether preferences exist
                const settingsResponse = await SettingsService.getSetting('userDashboard');
                let defaults = null;
                
                if (settingsResponse && settingsResponse.value) {
                    defaults = JSON.parse(settingsResponse.value);
                }
                
                // If we have preferences but some values are null, use defaults for those values
                if (preferences && defaults) {
                    return {
                        darkMode: preferences.darkMode ?? defaults.DarkMode,
                        formOpacity: preferences.formOpacity ?? defaults.FormOpacity,
                        formBlur: preferences.formBlur ?? defaults.FormBlur,
                        backgroundBlur: preferences.backgroundBlur ?? defaults.BackgroundImageBlur,
                        backgroundImage: preferences.backgroundImage ?? defaults.BackgroundImage,
                        baseColor: preferences.baseColor ?? defaults.BaseColor
                    };
                }
                
                // If we only have preferences, return them
                if (preferences) {
                    return preferences;
                }
                
                // If we only have defaults, return converted defaults
                if (defaults) {
                    return {
                        darkMode: defaults.DarkMode,
                        formOpacity: defaults.FormOpacity,
                        formBlur: defaults.FormBlur,
                        backgroundBlur: defaults.BackgroundImageBlur,
                        backgroundImage: defaults.BackgroundImage,
                        baseColor: defaults.BaseColor
                    };
                }
                
                return null;
            } catch (error) {
                // If user preferences not found, get default settings
                console.log('User preferences not found, loading defaults');
                const settingsResponse = await SettingsService.getSetting('userDashboard');
                if (settingsResponse && settingsResponse.value) {
                    const defaults = JSON.parse(settingsResponse.value);
                    // Convert PascalCase to camelCase
                    return {
                        darkMode: defaults.DarkMode,
                        formOpacity: defaults.FormOpacity,
                        formBlur: defaults.FormBlur,
                        backgroundBlur: defaults.BackgroundImageBlur,
                        backgroundImage: defaults.BackgroundImage,
                        baseColor: defaults.BaseColor
                    };
                }
                return null;
            }
        };

        const loadUserPreferences = async () => {
            if (user?.userId) {
                try {
                    // Get preferences with fallback
                    const prefs = await getPreferencesWithFallback(user.userId);
                    
                    if (prefs) {
                        // Get settings for fallback values
                        const settingsResponse = await SettingsService.getSetting('userDashboard');
                        let defaultBackgroundImage = '';
                        
                        if (settingsResponse && settingsResponse.value) {
                            const defaults = JSON.parse(settingsResponse.value);
                            defaultBackgroundImage = defaults.BackgroundImage || '';
                        }
                        
                        // Process background image URL - use settings background if prefs.backgroundImage is null
                        let backgroundImage = '';
                        if (prefs.backgroundImage) {
                            backgroundImage = `${API_BASE_URL}${encodeURI(prefs.backgroundImage)}`;
                        } else if (defaultBackgroundImage) {
                            // Use default background from settings if user preference is null
                            backgroundImage = `${API_BASE_URL}${encodeURI(defaultBackgroundImage)}`;
                        }
                        
                        const userPrefs = {
                            ...prefs,
                            backgroundImage,
                            menuCollapsed: prefs.menuCollapsed || false
                        };
                        
                        setUserPreferences(userPrefs);
                        updateCSSVariables(userPrefs);
                    }
                } catch (error) {
                    console.error('Error in preference loading:', error);
                }
            }
        };

        const loadDesktops = async () => {
            if (user?.userId) {
                try {
                    setIsLoading(true);
                    const accessibleDesktops = await DesktopAccessControlService.getAccessibleDesktops(user.userId);
                    setDesktops(accessibleDesktops);
                    
                    // Set initial desktop (home desktop)
                    const homeDesktop = accessibleDesktops.find(d => d.isHome);
                    if (homeDesktop) {
                        handleDesktopSelect(homeDesktop);
                    }
                } catch (error) {
                    console.error('Error loading accessible desktops:', error);
                    // Fallback to empty array if access control fails
                    setDesktops([]);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadUserPreferences();
        loadDesktops();
    }, [user?.userId]);

    const hexToRgb = (hex) => {
        if (!hex) return null;
        
        // Remove the hash if present
        hex = hex.replace('#', '');
        
        // Parse the hex values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // Check if parsing was successful
        if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
        
        return {r, g, b};
    };

    const updateCSSVariables = (preferences) => {
        // Remove all existing CSS variables first
        document.documentElement.style.removeProperty('--opacity');
        document.documentElement.style.removeProperty('--blur');
        document.documentElement.style.removeProperty('--base-color');
        document.documentElement.style.removeProperty('--base-color-rgb');
        document.documentElement.style.removeProperty('--bg-color');
        
        // Remove theme
        document.documentElement.removeAttribute('data-theme');
        
        // Force a reflow
        void document.documentElement.offsetHeight;
        
        // Set new values
        document.documentElement.style.setProperty('--opacity', preferences.formOpacity);
        document.documentElement.style.setProperty('--blur', preferences.formBlur);
        
        // Set theme
        document.documentElement.setAttribute('data-theme', preferences.darkMode ? 'dark' : 'light');
        
        // Force another reflow
        void document.documentElement.offsetHeight;
        
        // Handle base color
        if (preferences.baseColor) {
            document.documentElement.style.setProperty('--base-color', preferences.baseColor);
            const rgb = hexToRgb(preferences.baseColor);
            if (rgb) {
                document.documentElement.style.setProperty('--base-color-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
                
                // Set background color based on theme and base color
                if (preferences.darkMode) {
                    // Dark mode: Use the actual base color with opacity
                    document.documentElement.style.setProperty(
                        '--bg-color', 
                        `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${preferences.formOpacity})`
                    );
                } else {
                    // Light mode: Use a lighter tint of the base color
                    const lightFactor = 0.35;
                    const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
                    const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
                    const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
                    document.documentElement.style.setProperty(
                        '--bg-color', 
                        `rgba(${lightR}, ${lightG}, ${lightB}, ${preferences.formOpacity})`
                    );
                }
            }
        } else {
            // Remove the CSS variables if baseColor is null
            document.documentElement.style.removeProperty('--base-color');
            document.documentElement.style.removeProperty('--base-color-rgb');
            document.documentElement.style.removeProperty('--bg-color');
        }
    };

    // Add this new effect after the updateCSSVariables function
    useEffect(() => {
        // Create a style element for profile containers
        const styleEl = document.createElement('style');
        styleEl.id = 'profile-dynamic-styles';
        document.head.appendChild(styleEl);
        
        // Apply the same color logic as in DraggableSection
        const applyProfileStyles = (preferences) => {
            if (!preferences) return;
            
            let backgroundColor;
            if (preferences.baseColor) {
                const rgb = hexToRgb(preferences.baseColor);
                if (rgb) {
                    if (preferences.darkMode) {
                        // Dark mode: Use the actual base color with opacity
                        backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${preferences.formOpacity})`;
                    } else {
                        // Light mode: Use a lighter tint of the base color
                        const lightFactor = 0.35; // Keep 35% of the original color
                        const lightR = Math.round(rgb.r * lightFactor + 255 * (1 - lightFactor));
                        const lightG = Math.round(rgb.g * lightFactor + 255 * (1 - lightFactor));
                        const lightB = Math.round(rgb.b * lightFactor + 255 * (1 - lightFactor));
                        backgroundColor = `rgba(${lightR}, ${lightG}, ${lightB}, ${preferences.formOpacity})`;
                    }
                    
                    // Set the CSS content
                    styleEl.textContent = `
                        .profile-left-panel, 
                        .profile-right-panel,
                        .preferences-container,
                        .external-link-panel,
                        .internal-link-panel {
                            background: ${backgroundColor} !important;
                        }
                        .nav-pills .nav-link.active {
                            background-color: ${preferences.baseColor} !important;
                        }
                    `;
                }
            }
        };
        
        // Apply initial styles
        applyProfileStyles(userPreferences);
        
        // Clean up
        return () => {
            if (document.getElementById('profile-dynamic-styles')) {
                document.getElementById('profile-dynamic-styles').remove();
            }
        };
    }, [userPreferences]); // Re-run when preferences change

    const handleToggleMenu = () => {
        setIsMenuCollapsed(!isMenuCollapsed);
    };

    const handleDesktopSelect = async (desktop) => {
        // Close menu in mobile when clicking menu item
        if (window.innerWidth <= 768) {
            setIsMenuCollapsed(false);
        }
        setIsChangingDesktop(true);
        try {
            // Clear previous state regardless of desktop type
            setPanels([]);
            setSectionVisibility({});
            setLandingItems([]);
            setSelectedLandingItem(null);
            setShowLandingContent(false);
            setIframeLoading(true);
            setExternalLinkError(false);
            setLandingIframeLoading(false);
            setLandingIframeError(false);
            
            // Always navigate to default route first to clear any previous component state
            navigate("/user-dashboard/");
            
            setCurrentDesktop(desktop);
            
            // Only load panels if it's a Dashboard type
            if (desktop.desktopType === 'Dashboard') {
                let desktopPanels;
                let userPanelPreferences;
                try {
                    // Parallelize API calls for better performance
                    [desktopPanels, userPanelPreferences] = await Promise.all([
                        desktopService.getDesktopPanels(desktop.id),
                        userPanelPreferenceService.getDesktopLayout(user.userId, desktop.id)
                    ]);
                    
                    if (userPanelPreferences && userPanelPreferences.length > 0) {
                        // If user preferences exist, merge them with desktop panels
                        const panelsWithPreferences = desktopPanels.map(panel => {
                            const preference = userPanelPreferences.find(p => p.panelId === panel.id);
                            return {
                                ...panel,
                                position: preference?.position || panel.position || 'left',
                                size: preference?.size || panel.size || 'medium',
                                order: preference?.order || panel.order || 0
                            };
                        }).sort((a, b) => a.order - b.order);

                        setPanels(panelsWithPreferences);
                        
                        const visibilityState = {};
                        panelsWithPreferences.forEach(panel => {
                            visibilityState[panel.id] = true;
                        });
                        setSectionVisibility(visibilityState);
                    } else {
                        // If no user preferences exist, use default panel layout from desktop
                        const defaultPanels = desktopPanels.map((panel, index) => ({
                            ...panel,
                            position: panel.position || (index % 3 === 0 ? 'left' : index % 3 === 1 ? 'center' : 'right'),
                            size: panel.size || 'medium',
                            order: panel.order || Math.floor(index / 3)
                        }));

                        setPanels(defaultPanels);
                        
                        const visibilityState = {};
                        defaultPanels.forEach(panel => {
                            visibilityState[panel.id] = true;
                        });
                        setSectionVisibility(visibilityState);
                    }
                } catch (error) {
                    console.error('Error loading panel preferences:', error);
                    // If there's an error, just use the desktop panels with default layout
                    const defaultPanels = desktopPanels.map((panel, index) => ({
                        ...panel,
                        position: panel.position || (index % 3 === 0 ? 'left' : index % 3 === 1 ? 'center' : 'right'),
                        size: panel.size || 'medium',
                        order: panel.order || Math.floor(index / 3)
                    }));

                    setPanels(defaultPanels);
                    
                    const visibilityState = {};
                    defaultPanels.forEach(panel => {
                        visibilityState[panel.id] = true;
                    });
                    setSectionVisibility(visibilityState);
                }
            } else if (desktop.desktopType === 'Landing Page') {
                // Clear panels and load landing items
                try {
                    const items = await desktopService.getLandingItems(desktop.id);
                    
                    // Keep the tree structure for folder-like navigation
                    setLandingItems(items);
                    setCurrentItems(items);
                    setCurrentPath([]);
                } catch (error) {
                    console.error('Error loading landing items:', error);
                    setLandingItems([]);
                    setCurrentItems([]);
                }
            } else if (desktop.desktopType === 'Internal Link' && desktop.pageCode) {
                // For Internal Link, use the menu config to navigate after a short delay
                // This ensures the previous state is cleared first
                setTimeout(() => {
                    const path = menuConfig[desktop.pageCode] || "/user-dashboard/";
                    navigate(path);
                }, 0);
            }
            // For External Link type, we don't need to do anything special as it's handled in the render
        } catch (error) {
            console.error('Error in handleDesktopSelect:', error);
            // Set error state and show error message to user
            setExternalLinkError(true);
        } finally {
            setIsChangingDesktop(false);
            setIframeLoading(false);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const { source, destination } = result;
        const sourcePosition = source.droppableId;
        const destPosition = destination.droppableId;
        const panelId = parseInt(result.draggableId);
        const sourceIndex = source.index;
        const destIndex = destination.index;

        // Create a new array of panels
        let updatedPanels = [...panels];

        // Get panels in source position
        const sourcePanels = updatedPanels
            .filter(p => p.position === sourcePosition)
            .sort((a, b) => a.order - b.order);

        // Get panels in destination position
        const destPanels = sourcePosition === destPosition
            ? sourcePanels
            : updatedPanels
                .filter(p => p.position === destPosition)
                .sort((a, b) => a.order - b.order);

        // Find the panel being moved
        const movedPanel = sourcePanels[sourceIndex];
        if (!movedPanel) return;

        // Remove panel from source position
        sourcePanels.splice(sourceIndex, 1);

        // Insert panel at destination position
        if (sourcePosition === destPosition) {
            sourcePanels.splice(destIndex, 0, { ...movedPanel, position: destPosition });
            
            // Update orders in the same column
            sourcePanels.forEach((panel, index) => {
                panel.order = index;
            });
        } else {
            destPanels.splice(destIndex, 0, { ...movedPanel, position: destPosition });
            
            // Update orders in both columns
            sourcePanels.forEach((panel, index) => {
                panel.order = index;
            });
            
            destPanels.forEach((panel, index) => {
                panel.order = index;
            });
        }

        // Create final updated panels array
        updatedPanels = updatedPanels.filter(p => 
            p.position !== sourcePosition && p.position !== destPosition
        );
        updatedPanels = [
            ...updatedPanels,
            ...sourcePanels,
            ...(sourcePosition === destPosition ? [] : destPanels)
        ];

        // Update the state immediately
        setPanels(updatedPanels);

        try {
            const desktopId = currentDesktop.id;
            
            // Prepare the layout data
            const layoutData = {
                desktopId: desktopId,
                panelPreferences: updatedPanels.map(panel => ({
                    panelId: panel.id,
                    position: panel.position,
                    size: panel.size || 'medium',
                    order: panel.order
                }))
            };

            // Save the layout with user ID
            await userPanelPreferenceService.saveDesktopLayout(user.userId, layoutData);
        } catch (error) {
            console.error('Error saving panel positions:', error);
            // Revert to the previous state if save fails
            setPanels(panels);
        }
    };

    const handleAnnouncementClose = () => {
        setShowAnnouncement(false);
    };

    const handleThemeChange = async (darkMode) => {
        const newPreferences = { ...userPreferences, darkMode };
        setUserPreferences(newPreferences);
        updateCSSVariables(newPreferences);
        try {
            const formData = new FormData();
            formData.append('darkMode', darkMode.toString());
            formData.append('formOpacity', userPreferences.formOpacity.toString());
            formData.append('formBlur', userPreferences.formBlur.toString());
            formData.append('backgroundBlur', userPreferences.backgroundBlur.toString());
            if (userPreferences.baseColor) {
                formData.append('baseColor', userPreferences.baseColor);
            }
            await userPreferenceService.saveUserPreference(user.userId, formData);
        } catch (error) {
            console.error('Error updating theme:', error);
        }
    };

    const handleOpacityChange = async (formOpacity) => {
        const newPreferences = { ...userPreferences, formOpacity };
        setUserPreferences(newPreferences);
        updateCSSVariables(newPreferences);
        try {
            const formData = new FormData();
            formData.append('darkMode', userPreferences.darkMode.toString());
            formData.append('formOpacity', formOpacity.toString());
            formData.append('formBlur', userPreferences.formBlur.toString());
            formData.append('backgroundBlur', userPreferences.backgroundBlur.toString());
            if (userPreferences.baseColor) {
                formData.append('baseColor', userPreferences.baseColor);
            }
            await userPreferenceService.saveUserPreference(user.userId, formData);
        } catch (error) {
            console.error('Error updating opacity:', error);
        }
    };

    const handleBlurChange = async (formBlur) => {
        const newPreferences = { ...userPreferences, formBlur };
        setUserPreferences(newPreferences);
        updateCSSVariables(newPreferences);
        try {
            const formData = new FormData();
            formData.append('darkMode', userPreferences.darkMode.toString());
            formData.append('formOpacity', userPreferences.formOpacity.toString());
            formData.append('formBlur', formBlur.toString());
            formData.append('backgroundBlur', userPreferences.backgroundBlur.toString());
            if (userPreferences.baseColor) {
                formData.append('baseColor', userPreferences.baseColor);
            }
            await userPreferenceService.saveUserPreference(user.userId, formData);
        } catch (error) {
            console.error('Error updating blur:', error);
        }
    };

    const handleBackgroundBlurChange = async (backgroundBlur) => {
        const newPreferences = { ...userPreferences, backgroundBlur };
        setUserPreferences(newPreferences);
        updateCSSVariables(newPreferences);
        try {
            const formData = new FormData();
            formData.append('darkMode', userPreferences.darkMode.toString());
            formData.append('formOpacity', userPreferences.formOpacity.toString());
            formData.append('formBlur', userPreferences.formBlur.toString());
            formData.append('backgroundBlur', backgroundBlur.toString());
            if (userPreferences.baseColor) {
                formData.append('baseColor', userPreferences.baseColor);
            }
            await userPreferenceService.saveUserPreference(user.userId, formData);
        } catch (error) {
            console.error('Error updating background blur:', error);
        }
    };

    const handleBaseColorChange = async (baseColor) => {
        const newPreferences = { ...userPreferences, baseColor };
        setUserPreferences(newPreferences);
        updateCSSVariables(newPreferences);
        
        try {
            const formData = new FormData();
            formData.append('darkMode', userPreferences.darkMode.toString());
            formData.append('formOpacity', userPreferences.formOpacity.toString());
            formData.append('formBlur', userPreferences.formBlur.toString());
            formData.append('backgroundBlur', userPreferences.backgroundBlur.toString());
            if (baseColor !== null) {
                formData.append('baseColor', baseColor);
            }
            await userPreferenceService.saveUserPreference(user.userId, formData);
        } catch (error) {
            console.error('Error updating base color:', error);
        }
    };

    const handlePanelVisibilityChange = (panelId, action = 'toggle') => {
        setSectionVisibility(prev => {
            const newState = { ...prev };
            if (action === 'close') {
                newState[panelId] = false;
            } else if (action === 'toggle') {
                newState[panelId] = !prev[panelId];
            }
            return newState;
        });
    };

    const dashboardStyle = {
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: userPreferences.backgroundImage ? `url(${userPreferences.backgroundImage})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            filter: `blur(${(userPreferences.backgroundBlur || 0) * 10}px)`,
            zIndex: -1
        }
    };

    const getPageIcon = (page) => {
        if (page.pageIconImage) {
            return (
                <img
                    src={page.pageIconImage.startsWith('http') ? page.pageIconImage : `${API_BASE_URL}${page.pageIconImage}`}
                    alt={page.pageName}
                    style={{ width: 32, height: 32, marginBottom: 8 }}
                />
            );
        }
        if (page.pageIconCode) {
            const iconName = page.pageIconCode.startsWith('Fa') ? page.pageIconCode : `Fa${page.pageIconCode}`;
            const IconComponent = FaIcons[iconName] || FaTh;
            return <IconComponent style={{ fontSize: '2rem', marginBottom: 8 }} />;
        }
        return <FaTh style={{ fontSize: '2rem', marginBottom: 8 }} />;
    };

    const handlePageClick = (page) => {
        // Close menu in mobile when clicking menu item
        if (window.innerWidth <= 768) {
            setIsMenuCollapsed(false);
        }

        // Find the main panel
        const mainPanel = panels.find(p => p.code === 'Main');
        
        // If main panel exists and is hidden, show it
        if (mainPanel && !sectionVisibility[mainPanel.id]) {
            setSectionVisibility(prev => ({
                ...prev,
                [mainPanel.id]: true
            }));
        }

        // Navigate to the page
        navigate(`/user-dashboard/${page.pageCode}`);
    };

    useEffect(() => {
        const loadAccessiblePages = async () => {
            if (user?.userId && panels.some(panel => panel.code === 'Modules')) {
                try {
                    console.log('Fetching accessible pages for user:', user.userId);
                    const pages = await AccessControlService.getUserAccessiblePages(user.userId);
                    console.log('Accessible pages fetched:', pages);
                    setAccessiblePages(pages);
                } catch (error) {
                    console.error('Error fetching accessible pages:', error);
                    setAccessiblePages([]);
                }
            }
        };
        
        loadAccessiblePages();
    }, [user, panels]);

    const formatExternalUrl = (url) => {
        if (!url) return '';
        
        // Remove leading/trailing whitespace
        let formattedUrl = url.trim();
        
        try {
            // Check if it's a valid URL
            const urlObj = new URL(formattedUrl);
            
            // Check if it's trying to load the same origin
            const isSameOrigin = urlObj.origin === window.location.origin;
            if (isSameOrigin) {
                console.warn('DEBUG - Attempting to load same origin URL:', formattedUrl);
                throw new Error('Cannot load same origin URL in iframe');
            }

            // List of domains that are known to block iframe embedding
            const restrictedDomains = [
                'login.microsoftonline.com',
                'login.live.com',
                'login.microsoft.com',
                'account.microsoft.com',
                'portal.azure.com',
                'portal.office.com',
                'outlook.office.com',
                'outlook.office365.com',
                'teams.microsoft.com',
                'accounts.google.com',
                'mail.google.com',
                'drive.google.com',
                'docs.google.com'
            ];

            // Check if URL is from a known restricted domain
            if (restrictedDomains.some(domain => urlObj.hostname.toLowerCase() === domain.toLowerCase())) {
                console.warn('DEBUG - URL belongs to a domain that blocks iframes:', urlObj.hostname);
                throw new Error('Domain blocks iframe embedding');
            }
            
            return formattedUrl;
        } catch (error) {
            // If URL is invalid or same origin, try to add https://
            if (!formattedUrl.match(/^https?:\/\//i)) {
                formattedUrl = `https://${formattedUrl}`;
            }
            return formattedUrl;
        }
    };

    const handleLandingItemClick = (item) => {
        if (item.isParent && item.children && item.children.length > 0) {
            // This is a category - navigate into it like a folder
            setCurrentPath([...currentPath, item]);
            setCurrentItems(item.children);
        } else if (item.linkType) {
            // This is an actual item - handle it normally
            setShowLandingContent(true);
            
            if ((item.linkType === 'External' || item.linkType === 'ExternalLink') && item.url) {
                try {
                    // This will throw if the URL is from a restricted domain
                    formatExternalUrl(item.url);
                    // If we get here, it's not a known restricted URL
                    setSelectedLandingItem(item);
                    setLandingIframeLoading(true);
                    setLandingIframeError(false);
                } catch (error) {
                    // Known restricted URL - show error immediately
                    setSelectedLandingItem(item);
                    setLandingIframeLoading(false);
                    setLandingIframeError(true);
                }
            } else if (item.linkType === 'Internal' && item.pageCode) {
                const path = menuConfig[item.pageCode] || "/user-dashboard/";
                navigate(path);
                console.log('DEBUG - Navigating to:', path);
                return;
            } else if (item.linkType === 'File') {
                // Handle file type landing items
                setSelectedLandingItem(item);
            }
        }
    };

    const handleBackToLanding = () => {
        setShowLandingContent(false);
        setSelectedLandingItem(null);
        setLandingIframeLoading(false);
        setLandingIframeError(false);
    };

    const handleBackToParent = () => {
        if (currentPath.length > 0) {
            const newPath = currentPath.slice(0, -1);
            setCurrentPath(newPath);
            
            // Navigate back to parent items
            let items = landingItems;
            for (const pathItem of newPath) {
                const found = items.find(item => item.id === pathItem.id);
                if (found && found.children) {
                    items = found.children;
                } else {
                    break;
                }
            }
            setCurrentItems(items);
        }
    };

    const handleIframeLoad = (e) => {
        try {
            // Try to access the iframe content to see if it loaded
            const iframe = e.target;
            if (iframe.contentWindow.location.href) {
                setIframeLoading(false);
                setExternalLinkError(false);
            } else {
                setIframeLoading(false);
                setExternalLinkError(true);
            }
        } catch (error) {
            // If we can't access the iframe content, show error
            setIframeLoading(false);
            setExternalLinkError(true);
        }
    };

    const handleIframeError = () => {
        setIframeLoading(false);
        setExternalLinkError(true);
    };

    // Update the effect for desktop external links
    useEffect(() => {
        if (currentDesktop?.desktopType === 'External Link' && currentDesktop?.url) {
            setIframeLoading(true);
            setExternalLinkError(false);
            
            // Try to fetch the URL first to check if it's accessible
            fetch(formatExternalUrl(currentDesktop.url), { mode: 'no-cors' })
                .then(() => {
                    setIframeLoading(false);
                    setExternalLinkError(false);
                })
                .catch(() => {
                    setIframeLoading(false);
                    setExternalLinkError(true);
                });
        }
    }, [currentDesktop]);

    // Effect for landing item external links
    useEffect(() => {
        if ((selectedLandingItem?.linkType === 'External' || selectedLandingItem?.linkType === 'ExternalLink') && selectedLandingItem?.url) {
            setLandingIframeLoading(true);
            setLandingIframeError(false);
            
            try {
                // This will throw if the URL is from a restricted domain
                formatExternalUrl(selectedLandingItem.url);
                
                // Try to fetch the URL first to check if it's accessible
                fetch(formatExternalUrl(selectedLandingItem.url), { mode: 'no-cors' })
                    .then(() => {
                        setLandingIframeLoading(false);
                        setLandingIframeError(false);
                    })
                    .catch(() => {
                        setLandingIframeLoading(false);
                        setLandingIframeError(true);
                    });
            } catch (error) {
                // Known restricted URL - show error immediately
                setLandingIframeLoading(false);
                setLandingIframeError(true);
            }
        } else {
            // Reset loading state if not an external link
            setLandingIframeLoading(false);
            setLandingIframeError(false);
        }
    }, [selectedLandingItem]);

    // Separate handlers for landing items
    const handleLandingIframeLoad = (e) => {
        console.log('DEBUG - Iframe load event triggered');
        setLandingIframeLoading(false);  // Always clear loading state
        
        try {
            // Try to access the iframe content
            const iframe = e.target;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            
            // Check if it's a Firefox security error page
            if (iframeDoc.title.includes("Can't Open This Page") || 
                iframeDoc.body.textContent.includes("Firefox Can't Open This Page") ||
                iframeDoc.body.textContent.includes("will not allow Firefox to display the page")) {
                console.log('DEBUG - Detected Firefox security error page');
                setLandingIframeError(true);
                return;
            }

            // If we get here, the iframe loaded successfully
            console.log('DEBUG - Iframe loaded successfully');
            setLandingIframeError(false);
        } catch (error) {
            // If we can't access the iframe content, it's probably a security error
            console.log('DEBUG - Cannot access iframe content:', error);
            setLandingIframeError(true);
        }
    };

    const handleLandingIframeError = (error) => {
        console.error('DEBUG - Iframe error:', error);
        setLandingIframeLoading(false);
        setLandingIframeError(true);
    };

    // Add effect to handle route changes
    useEffect(() => {
        // If we have a selected internal item and we're at the dashboard root,
        // it means we've navigated back, so clear the states
        if (selectedLandingItem?.linkType === 'Internal' && location.pathname === '/user-dashboard/') {
            setSelectedLandingItem(null);
            setShowLandingContent(false);
        }
    }, [location.pathname, selectedLandingItem]);

    // Update the effect for landing item iframe
    useEffect(() => {
        let iframeTimeout;
        
        if (selectedLandingItem?.linkType === 'External' && selectedLandingItem?.url) {
            setIframeLoading(true);
            setExternalLinkError(false);
        }
        
        return () => {
            if (iframeTimeout) {
                clearTimeout(iframeTimeout);
            }
        };
    }, [selectedLandingItem]);

    // Add cleanup effect for route changes
    useEffect(() => {
        return () => {
            // Clean up all loading states when component unmounts
            setIsLoading(false);
            setIsChangingDesktop(false);
            setIframeLoading(false);
            setExternalLinkError(false);
        };
    }, []);

    const renderFileContent = (url) => {
        if (!url) return null;

        // Get the file extension
        const extension = url.split('.').pop().toLowerCase();

        const containerStyle = {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 180px)',
            padding: '15px 5px 15px 5px'
        };

        // Image files
        const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
        if (imageTypes.includes(extension)) {
            return (
                <div style={containerStyle}>
                    <ImageViewer url={url} />
                </div>
            );
        }

        // Video files
        const videoTypes = ['mp4', 'webm', 'ogg'];
        if (videoTypes.includes(extension)) {
            return (
                <div className="file-viewer video-viewer" style={containerStyle}>
                    <div style={{ 
                        width: '100%', 
                        maxWidth: '1200px',
                        maxHeight: '80vh',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <video 
                            controls 
                            style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        >
                            <source src={`${API_BASE_URL}${url}`} type={`video/${extension}`} />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            );
        }

        // Audio files
        const audioTypes = ['mp3', 'wav', 'ogg'];
        if (audioTypes.includes(extension)) {
            return (
                <div className="file-viewer audio-viewer" style={containerStyle}>
                    <div style={{ 
                        width: '100%',
                        maxWidth: '600px',
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <audio 
                            controls 
                            style={{ width: '100%' }}
                        >
                            <source src={`${API_BASE_URL}${url}`} type={`audio/${extension}`} />
                            Your browser does not support the audio tag.
                        </audio>
                    </div>
                </div>
            );
        }

        // PDF files
        if (extension === 'pdf') {
            return (
                <div className="file-viewer pdf-viewer" style={containerStyle}>
                    <div style={{ 
                        width: '100vw',
                        maxWidth: '1200px',
                        height: '80vh',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <iframe
                            src={`${API_BASE_URL}${url}`}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                border: 'none'
                            }}
                            title="PDF Viewer"
                        />
                    </div>
                </div>
            );
        }

        // For other file types, show download button
        return (
            <div className="file-viewer default-viewer" style={containerStyle}>
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <FaIcons.FaFile size={60} className="mb-4 text-primary" />
                    <h4>File Preview Not Available</h4>
                    <p className="mb-4">This file type cannot be previewed directly. Please download to view.</p>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => window.open(`${API_BASE_URL}${url}`, '_blank')}
                    >
                        <FaIcons.FaDownload className="me-2" />
                        Download File
                    </Button>
                </div>
            </div>
        );
    };

    // Update the landing item content rendering
    const renderLandingItemContent = () => {
        if (!selectedLandingItem) return null;

        if (selectedLandingItem.linkType === 'File' && selectedLandingItem.url) {
            return (
                <div className="desktop-panel" style={{ height: '100%' }}>
               <div className="desktop-panel-header">
                                        <div className="desktop-back-button">
                                          <Button
                                                    variant="link"
                                                    desktop-back-button
                                                    onClick={handleBackToLanding}
                                                >
                                                    <FaIcons.FaArrowLeft /> Back
                                                </Button>
                                                </div>
                                            <div >
                                            <h6>{selectedLandingItem?.name || 'External Link'}</h6>
                                            <OverlayTrigger
                                                placement="bottom"
                                                delay={{ show: 200, hide: 100 }}
                                                overlay={
                                                    <Tooltip id={`tooltip-${selectedLandingItem.id}`} className="bs-tooltip-bottom">
                                                        {selectedLandingItem.description || 'No description available'}
                                                    </Tooltip>
                                                }
                                            >
                                                <div className="desktop-description">
                                                    {selectedLandingItem.description}
                                                </div>
                                            </OverlayTrigger>
                                            </div>
                                        
                                        </div>
                    <div className="landing-file-content" style={{ 
                        height: 'calc(100% - 60px)', // Reduced from 80px
                        overflow: 'auto'
                    }}>
                        {renderFileContent(selectedLandingItem.url)}
                    </div>
                </div>
            );
        }

        return null;
    };

    // Add CSS styles for the containers
    const containerStyle = {
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const panelStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    const contentStyle = {
        flex: 1,
        position: 'relative',
        overflow: 'hidden'
    };

    const iframeStyle = {
        width: '100%',
        height: '100%',
        border: 'none',
        position: 'absolute',
        top: 0,
        left: 0
    };

    useEffect(() => {
        const handleMobileClick = (event) => {
            // Only in mobile mode
            if (window.innerWidth <= 768) {
                const menu = document.querySelector('.user-dashboard-left-menu');
                const toggleButton = document.querySelector('.mobile-menu-toggle');
                
                // In mobile: collapsed=true means menu is visible, collapsed=false means hidden
                // If menu is visible (collapsed=true) and click is outside menu and toggle
                if (isMenuCollapsed && menu && toggleButton && 
                    !menu.contains(event.target) && !toggleButton.contains(event.target)) {
                    setIsMenuCollapsed(false); // Hide the menu
                }
            }
        };

        document.addEventListener('click', handleMobileClick);
        return () => document.removeEventListener('click', handleMobileClick);
    }, [isMenuCollapsed]);

    const handleProfileClick = () => {
        // Close menu in mobile when clicking profile
        if (window.innerWidth <= 768) {
            setIsMenuCollapsed(false);
        }
        setShowProfileModal(true);
    };

    return (
        <div className="user-dashboard" data-theme={userPreferences.darkMode ? 'dark' : 'light'}>
            {/* Single background layer with proper blur */}
            <div 
                className="user-dashboard-background"
                style={{
                    backgroundImage: userPreferences.backgroundImage ? `url(${userPreferences.backgroundImage})` : 'none',
                    backgroundColor: 'transparent',
                    filter: `blur(${(userPreferences.backgroundBlur || 0) * 8}px)`,
                    transform: 'scale(1.1)', // Prevent blur edges from showing
                }}
            />
            <UserLeftMenu 
                isCollapsed={isMenuCollapsed}
                onToggle={handleToggleMenu}
                darkMode={userPreferences.darkMode}
                opacity={userPreferences.formOpacity}
                blur={userPreferences.formBlur}
                baseColor={userPreferences.baseColor}
                desktops={desktops}
                currentDesktop={currentDesktop}
                onDesktopSelect={handleDesktopSelect}
                onProfileClick={handleProfileClick}
            />
            
            <div className={`dashboard-layout ${isMenuCollapsed ? 'menu-collapsed' : ''}`}>
                <UserNavbar 
                    darkMode={userPreferences.darkMode}
                    onThemeChange={handleThemeChange}
                    opacity={userPreferences.formOpacity}
                    onOpacityChange={handleOpacityChange}
                    blur={userPreferences.formBlur}
                    onBlurChange={handleBlurChange}
                    baseColor={userPreferences.baseColor}
                    onBaseColorChange={handleBaseColorChange}
                    backgroundBlur={userPreferences.backgroundBlur}
                    onBackgroundBlurChange={handleBackgroundBlurChange}
                    userId={user?.userId}
                    sectionVisibility={sectionVisibility}
                    onSectionVisibilityChange={handlePanelVisibilityChange}
                    panels={panels}
                />
                
                {showAnnouncement && (
                    <Announcement 
                        onClose={handleAnnouncementClose}
                        darkMode={userPreferences.darkMode}
                        opacity={userPreferences.formOpacity}
                        blur={userPreferences.formBlur}
                        baseColor={userPreferences.baseColor}
                    />
                )}

                {isLoading || isChangingDesktop ? (
                    <div className="spinner-wrapper">
                        <Spinner />
                    </div>
                ) : currentDesktop?.desktopType === 'Dashboard' ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <div className="main-content">
                            <Droppable droppableId="left">
                                {(provided) => (
                                    <div 
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="left-column"
                                    >
                                        {panels
                                            .filter(panel => panel.position === 'left')
                                            .sort((a, b) => a.order - b.order)
                                            .map((panel, index) => (
                                                <DraggableSection
                                                    key={panel.id}
                                                    section={{...panel, position: 'left'}}
                                                    index={index}
                                                    darkMode={userPreferences.darkMode}
                                                    opacity={userPreferences.formOpacity}
                                                    blur={userPreferences.formBlur}
                                                    baseColor={userPreferences.baseColor}
                                                    isVisible={sectionVisibility[panel.id]}
                                                    onClose={() => handlePanelVisibilityChange(panel.id, 'close')}
                                                    onVisibilityChange={handlePanelVisibilityChange}
                                                    isMenuCollapsed={isMenuCollapsed}
                                                    panels={panels}
                                                    sectionVisibility={sectionVisibility}
                                                    onSectionVisibilityChange={handlePanelVisibilityChange}
                                                >
                                                    {panel.code === 'Modules' ? (
                                                        <div className="row">
                                                            {accessiblePages && accessiblePages.length > 0 ? (
                                                                accessiblePages.map(page => (
                                                                    <div
                                                                        className="col-6 col-md-4 col-lg-3"
                                                                        key={page.pageId}
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handlePageClick(page)}
                                                                    >
                                                                        <div className="text-center p-2 h-100 module-item">
                                                                            {getPageIcon(page)}
                                                                            <div>{page.pageName}</div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="col-12 text-center text-muted py-4">
                                                                    <p>No accessible pages found.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        panel.code === 'Main' && <Outlet />
                                                    )}
                                                </DraggableSection>
                                            ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            
                            <Droppable droppableId="center">
                                {(provided) => (
                                    <div 
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="center-column"
                                    >
                                        {panels
                                            .filter(panel => panel.position === 'center')
                                            .sort((a, b) => a.order - b.order)
                                            .map((panel, index) => (
                                                <DraggableSection
                                                    key={panel.id}
                                                    section={{...panel, position: 'center'}}
                                                    index={index}
                                                    darkMode={userPreferences.darkMode}
                                                    opacity={userPreferences.formOpacity}
                                                    blur={userPreferences.formBlur}
                                                    baseColor={userPreferences.baseColor}
                                                    isVisible={sectionVisibility[panel.id]}
                                                    onClose={() => handlePanelVisibilityChange(panel.id, 'close')}
                                                    onVisibilityChange={handlePanelVisibilityChange}
                                                    isMenuCollapsed={isMenuCollapsed}
                                                    panels={panels}
                                                    sectionVisibility={sectionVisibility}
                                                    onSectionVisibilityChange={handlePanelVisibilityChange}
                                                >
                                                    {panel.code === 'Modules' ? (
                                                        <div className="row">
                                                            {accessiblePages && accessiblePages.length > 0 ? (
                                                                accessiblePages.map(page => (
                                                                    <div
                                                                        className="col-6 col-md-4 col-lg-3 mb-3"
                                                                        key={page.pageId}
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handlePageClick(page)}
                                                                    >
                                                                        <div className="text-center p-2 h-100 module-item">
                                                                            {getPageIcon(page)}
                                                                            <div className="mt-2">{page.pageName}</div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="col-12 text-center text-muted py-4">
                                                                    <p>No accessible pages found.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        panel.code === 'Main' && <Outlet />
                                                    )}
                                                </DraggableSection>
                                            ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            
                            <Droppable droppableId="right">
                                {(provided) => (
                                    <div 
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="right-column"
                                    >
                                        {panels
                                            .filter(panel => panel.position === 'right')
                                            .sort((a, b) => a.order - b.order)
                                            .map((panel, index) => (
                                                <DraggableSection
                                                    key={panel.id}
                                                    section={{...panel, position: 'right'}}
                                                    index={index}
                                                    darkMode={userPreferences.darkMode}
                                                    opacity={userPreferences.formOpacity}
                                                    blur={userPreferences.formBlur}
                                                    baseColor={userPreferences.baseColor}
                                                    isVisible={sectionVisibility[panel.id]}
                                                    onClose={() => handlePanelVisibilityChange(panel.id, 'close')}
                                                    onVisibilityChange={handlePanelVisibilityChange}
                                                    isMenuCollapsed={isMenuCollapsed}
                                                    panels={panels}
                                                    sectionVisibility={sectionVisibility}
                                                    onSectionVisibilityChange={handlePanelVisibilityChange}
                                                >
                                                    {panel.code === 'Modules' ? (
                                                        <div className="row">
                                                            {accessiblePages && accessiblePages.length > 0 ? (
                                                                accessiblePages.map(page => (
                                                                    <div
                                                                        className="col-6 col-md-4 col-lg-3 mb-3"
                                                                        key={page.pageId}
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => handlePageClick(page)}
                                                                    >
                                                                        <div className="text-center p-2 h-100 module-item">
                                                                            {getPageIcon(page)}
                                                                            <div className="mt-2">{page.pageName}</div>
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="col-12 text-center text-muted py-4">
                                                                    <p>No accessible pages found.</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        panel.code === 'Main' && <Outlet />
                                                    )}
                                                </DraggableSection>
                                            ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    </DragDropContext>
                ) : currentDesktop?.desktopType === 'Landing Page' ? (
                    <div className="desktop-container">
                        
                            {!showLandingContent ? (
                                <div className="desktop-panel">
                                    {currentPath.length > 0 && (
                                        <div className="desktop-panel-header">
                                            <div className="desktop-back-button">
                                                <Button
                                                    variant="link"
                                                    onClick={handleBackToParent}
                                                >
                                                    <FaIcons.FaArrowLeft /> Back
                                                </Button>
                                            </div>
                                            <div>
                                                <h6>{currentPath[currentPath.length - 1]?.name || 'Category'}</h6>
                                                <div className="breadcrumb" style={{ fontSize: '12px', color: '#666' }}>
                                                    Root {currentPath.map((item, index) => (
                                                        <span key={item.id}>
                                                            {' > '}
                                                            <span style={{ color: '#333' }}>{item.name}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="desktop-panel-content">
                                        <div className="landing-page-grid">
                                            {currentItems && currentItems.length > 0 ? (
                                                currentItems.map((item, index) => (
                                                    <div
                                                        key={item.id || index}
                                                        className="landing-item-wrapper"
                                                    >
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            delay={{ show: 200, hide: 100 }}
                                                            overlay={
                                                                <Tooltip id={`tooltip-${item.id}`} className="bs-tooltip-bottom">
                                                                    {item.description || 'No description available'}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <div 
                                                                className="landing-item"
                                                                onClick={() => handleLandingItemClick(item)}
                                                            >
                                                                <div className="landing-item-icon">
                                                                    {item.iconImage ? (
                                                                        <img 
                                                                            src={item.iconImage.startsWith('http') ? item.iconImage : `${API_BASE_URL}${item.iconImage}`}
                                                                            alt={item.name}
                                                                            className="landing-icon-image"
                                                                        />
                                                                    ) : item.iconCode ? (
                                                                        (() => {
                                                                            const iconName = `Fa${item.iconCode}`;
                                                                            const IconComponent = FaIcons[iconName] || FaTh;
                                                                            return <IconComponent className="landing-icon" />;
                                                                        })()
                                                                    ) : (
                                                                        <FaTh className="landing-icon" />
                                                                    )}
                                                                </div>
                                                                <div className="landing-item-content">
                                                                    <h6 className="landing-item-title">{item.name}</h6>
                                                                </div>
                                                            </div>
                                                        </OverlayTrigger>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="no-items-message">
                                                    <p>No landing page items available.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : selectedLandingItem?.linkType === 'Internal' ? (
                                <div className="desktop-container">
                                    <div className="desktop-panel">
                                    <div className="desktop-panel-header">
                                        <div className="desktop-back-button">
                                          <Button
                                                    variant="link"
                                                    desktop-back-button
                                                    onClick={handleBackToLanding}
                                                >
                                                    <FaIcons.FaArrowLeft /> Back
                                                </Button>
                                                </div>
                                            <div >
                                            <h6>{selectedLandingItem?.name || 'Internal Link'}</h6>
                                            {selectedLandingItem?.description && (
                                                <p className="desktop-description">{selectedLandingItem.description}</p>
                                            )}
                                            </div>
                                        
                                        </div>
                                        <div className="desktop-panel-content">
                                            <Outlet />
                                        </div>
                                    </div>
                                </div>
                            ) : (selectedLandingItem?.linkType === 'External' || selectedLandingItem?.linkType === 'ExternalLink') ? (
                                <div className="desktop-panel">
                                    <div className="desktop-panel-header">
                                        <div className="desktop-back-button">
                                            <Button
                                                variant="link"
                                                desktop-back-button
                                                onClick={handleBackToLanding}
                                            >
                                                <FaIcons.FaArrowLeft /> Back
                                            </Button>
                                        </div>
                                        <div>
                                            <h6>{selectedLandingItem?.name || 'External Link'}</h6>
                                            {selectedLandingItem?.description && (
                                                <p className="desktop-description">{selectedLandingItem.description}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="desktop-panel-content">
                                        {landingIframeLoading ? (
                                            <div className="iframe-loading text-center p-5">
                                                <Spinner />
                                                <p className="mt-3">Loading external content...</p>
                                            </div>
                                        ) : landingIframeError ? (
                                            <div className="iframe-error-message text-center p-5">
                                                <FaIcons.FaExternalLinkAlt size={40} className="mb-3 text-primary" />
                                                <h4>External Link</h4>
                                                <p className="mb-4">This content cannot be displayed in the app. Click the button below to open in a new window.</p>
                                                <Button
                                                    variant="primary"
                                                    size="lg"
                                                    onClick={() => {
                                                        const url = selectedLandingItem?.url;
                                                        if (url) {
                                                            const trimmedUrl = url.trim();
                                                            const urlWithProtocol = trimmedUrl.match(/^https?:\/\//i) 
                                                                ? trimmedUrl 
                                                                : `https://${trimmedUrl}`;
                                                            window.open(urlWithProtocol, '_blank', 'noopener,noreferrer');
                                                        }
                                                    }}
                                                    className="mt-2"
                                                >
                                                    <FaIcons.FaExternalLinkAlt className="me-2" />
                                                    Open in New Window
                                                </Button>
                                            </div>
                                        ) : (
                                            <iframe
                                                src={formatExternalUrl(selectedLandingItem.url)}
                                                className="external-link-iframe"
                                                title={selectedLandingItem?.name || 'External Content'}
                                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-top-navigation"
                                                referrerPolicy="no-referrer"
                                                onLoad={handleLandingIframeLoad}
                                                onError={handleLandingIframeError}
                                            />
                                        )}
                                    </div>
                                </div>
                            ) : selectedLandingItem?.linkType === 'File' ? (
                                renderLandingItemContent()
                            ) : null}
                        
                    </div>
                ) : currentDesktop?.desktopType === 'Internal Link' ? (
                    <div className="desktop-container">
                        <div className="desktop-panel">
                            <div className="desktop-panel-header">
                                <h4>{currentDesktop?.name || 'Internal Link'}</h4>
                                {currentDesktop?.description && (
                                    <p className="desktop-description">{currentDesktop.description}</p>
                                )}
                            </div>
                            <div className="desktop-panel-content">
                                <Outlet />
                            </div>
                        </div>
                    </div>
                ) : currentDesktop?.desktopType === 'External Link' ? (
                    <div className="desktop-container">
                        <div className="desktop-panel">
                            <div className="desktop-panel-header">
                                <h6>{currentDesktop?.name || 'External Link'}</h6>
                                {currentDesktop?.description && (
                                    <p className="desktop-description">{currentDesktop.description}</p>
                                )}
                            </div>
                            <div className="desktop-panel-content">
                                {iframeLoading ? (
                                    <div className="iframe-loading text-center p-5">
                                        <Spinner />
                                        <p className="mt-3">Loading external content...</p>
                                    </div>
                                ) : externalLinkError ? (
                                    <div className="iframe-error-message text-center p-5">
                                        <FaIcons.FaExternalLinkAlt size={40} className="mb-3 text-primary" />
                                        <h4>External Link</h4>
                                        <p className="mb-4">This content cannot be displayed in the app. Click the button below to open in a new window.</p>
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={() => {
                                                console.log('Current Desktop:', currentDesktop);
                                                const url = currentDesktop?.url;
                                                console.log('Original URL:', url);
                                                if (url) {
                                                    const trimmedUrl = url.trim();
                                                    console.log('Trimmed URL:', trimmedUrl);
                                                    const urlWithProtocol = trimmedUrl.match(/^https?:\/\//i) 
                                                        ? trimmedUrl 
                                                        : `https://${trimmedUrl}`;
                                                    console.log('Final URL to open:', urlWithProtocol);
                                                    window.open(urlWithProtocol, '_blank', 'noopener,noreferrer');
                                                }
                                            }}
                                            className="mt-2"
                                        >
                                            <FaIcons.FaExternalLinkAlt className="me-2" />
                                            Open in New Window
                                        </Button>
                                    </div>
                                ) : (
                                    <iframe 
                                        ref={iframeRef}
                                        src={formatExternalUrl(currentDesktop.url)}
                                        className="external-link-iframe"
                                        title={currentDesktop?.name || 'External Content'}
                                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-top-navigation"
                                        referrerPolicy="no-referrer"
                                        onLoad={handleIframeLoad}
                                        onError={handleIframeError}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <Outlet />
                    </div>
                )}
            </div>

            <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Profile Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Profile isModal={true} onClose={() => setShowProfileModal(false)} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default UserDashboard; 